import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useSessionTimer } from '../../contexts/SessionTimerContext';

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const TimerContainer = styled.div`
  position: fixed;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
  z-index: 9999;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }
`;

const TimerIcon = styled.span`
  font-size: 16px;
`;

const TimerText = styled.span<{ isWarning: boolean }>`
  color: ${props => props.isWarning ? '#ffd700' : 'white'};
  animation: ${props => props.isWarning ? pulse : 'none'} 1s infinite;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  max-width: 380px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const ModalIcon = styled.div`
  font-size: 50px;
  margin-bottom: 15px;
`;

const ModalTitle = styled.h3`
  color: #333;
  margin: 0 0 10px 0;
  font-size: 20px;
`;

const ModalText = styled.p`
  color: #666;
  margin: 0 0 20px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  ` : `
    background: #f0f0f0;
    color: #666;
    
    &:hover {
      background: #e0e0e0;
    }
  `}
`;

const TimeDisplay = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #764ba2;
  margin: 15px 0;
`;

interface SessionTimerProps {
  sessionDuration?: number;
  warningTime?: number;
  onSessionExpired?: () => void;
  onSaveState?: () => Promise<void>;
}

const SessionTimer: React.FC<SessionTimerProps> = ({
  onSessionExpired,
  onSaveState
}) => {
  const {
    timeLeft,
    isWarning,
    showModal,
    setShowModal,
    extendSession,
    logout,
    isExtending,
    formatTime,
    sessionDuration
  } = useSessionTimer();

  const handleLogout = async () => {
    await logout(onSaveState, onSessionExpired);
  };

  return (
    <>
      <TimerContainer onClick={() => setShowModal(true)} title="Session Time Remaining">
        <TimerIcon>⏱️</TimerIcon>
        <TimerText isWarning={isWarning}>
          {formatTime(timeLeft)}
        </TimerText>
      </TimerContainer>

      {showModal && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <Modal>
            <ModalIcon>⏰</ModalIcon>
            <ModalTitle>Session Expiring Soon!</ModalTitle>
            <TimeDisplay>{formatTime(timeLeft)}</TimeDisplay>
            <ModalText>
              Your session will expire soon. Would you like to extend it by another {sessionDuration} minutes?
              <br /><br />
              <small style={{ color: '#888' }}>
                Don't worry - your progress will be saved automatically.
              </small>
            </ModalText>
            <ModalButtons>
              <Button onClick={handleLogout}>
                Logout
              </Button>
              <Button primary onClick={extendSession} disabled={isExtending}>
                {isExtending ? 'Extending...' : `Extend ${sessionDuration} min`}
              </Button>
            </ModalButtons>
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};

export default SessionTimer;
